import React, { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import requestClient from '../../../utils/requestClient';
import ArticleCard from '../../../components/Elements/Cards/ArticleCard';

import FormInput from '../../../components/Elements/Form/FormInput';
import FormFile from '../../../components/Elements/Form/FormFile';
import FormTextArea from '../../../components/Elements/Form/FormTextArea';
import FormRadio from '../../../components/Elements/Form/FormRadio';
import ErrorMessage from '../../../components/Elements/ErrorMessage';
import SuccessMessage from '../../../components/Elements/SuccessMessage';

const PDF_MAX_SIZE = 10485760; // 10MB
const VIDEO_MAX_SIZE = 26214400; // 25MB

const ResourceEdit = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState(null);

    const titleRef = useRef(null)
    const fileRef = useRef(null)
    const pdfRef = useRef(null)
    const videoRef = useRef(null)
    const publishDateRef = useRef(null)
    const additionalInfoRef = useRef(null)
    const visibilityRef = useRef(null)

    useEffect(() => {
        async function getData() {
            try {
                const response = await requestClient().get(`/api/v2/employer/resources/${id}/`)
        
                setData(response.data.data)
                setLoading(false)
            } catch (error) {
                console.error('Failed to load resource:', error)
                setLoading(false)
            }
        }
        getData();
    }, [id]);

    async function handleSubmit(event) {
        setSuccess(false)
        setErrors(null)
        
        event.preventDefault()

        if (pdfRef.current.files[0] && pdfRef.current.files[0].size > PDF_MAX_SIZE) {
            setErrors({ 'pdf_document': ['PDF file size must be less than 10MB'] });
            return;
        }

        if (videoRef.current.files[0] && videoRef.current.files[0].size > VIDEO_MAX_SIZE) {
            setErrors({ 'video': ['Video file size must be less than 25MB'] });
            return;
        }

        const formData = new FormData();
        formData.append('title', titleRef.current.value);
        if (fileRef.current.files[0]) {
            formData.append('image', fileRef.current.files[0]);
        }
        if (pdfRef.current.files[0]) {
            formData.append('pdf_document', pdfRef.current.files[0]);
        }
        if (videoRef.current.files[0]) {
            formData.append('video', videoRef.current.files[0]);
        }
        formData.append('publish_date', publishDateRef.current.value);
        formData.append('additional_information', additionalInfoRef.current.value);
        formData.append('accessible_to_employees', visibilityRef.current.checked);

        await requestClient().put(`/api/v2/employer/resources/${id}/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => {
            setSuccess(true)
        }).catch(error => {
            setErrors(error.response.data.error)
        })
    }

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!data) {
        return <div>Resource not found</div>;
    }

    return (
        <main>
            <section className="bg-white">
                <div className='container py-8 lg:py-12'>
                    <div className='border-b pb-8 flex lg:items-center lg:justify-between mb-8 gap-4 flex-col lg:flex-row'>
                        <h1>Edit resource</h1>
                    </div>
                    { success ? <SuccessMessage title="Success"  text="Your resource has been updated." className="mb-4" /> : null }
                    { errors ? <ErrorMessage title="Something is not right"  errors={errors} className="mb-4" /> : null}
                    <form onSubmit={handleSubmit}>
                        <div className='grid gap-5'>
                            <FormInput 
                                type="text" 
                                name="title" 
                                label="Title" 
                                inputRef={titleRef} 
                                props={{'required': 'required'}} 
                                value={data.title || ''}
                            />
                            <FormFile 
                                id="file" 
                                name="image" 
                                label="Image" 
                                inputRef={fileRef} 
                            />
                            <FormInput 
                                type="date" 
                                name="publishDate" 
                                label="Publish date" 
                                inputRef={publishDateRef} 
                                props={{'required': 'required'}} 
                                value={data.publish_date ? data.publish_date.split('T')[0] : ''}
                            />
                            <FormFile 
                                id="pdf" 
                                name="pdf_document" 
                                label="Document" 
                                inputRef={pdfRef} 
                            />
                            <FormFile 
                                id="video" 
                                name="video" 
                                label="Video" 
                                inputRef={videoRef} 
                            />
                            <FormTextArea 
                                name="additionalInformation" 
                                label="Additional information" 
                                inputRef={additionalInfoRef}
                                value={data.additional_information || ''}
                                props={{ rows: 12 }}
                            />
                            <FormRadio 
                                label="Visible to employees?" 
                                name="accessible_to_employees" 
                                inputRef={visibilityRef} 
                                value={data.accessible_to_employees || false}
                            />
                            <button className="btn btn-purple block">Update</button>
                        </div>
                    </form>
                </div>
            </section>
        </main>
    );
};

export default ResourceEdit;